import {
  HttpContext, HttpContextToken, HttpErrorResponse, HttpEvent, HttpInterceptorFn, HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, TimeoutError, retry, tap, throwError, timer } from 'rxjs';
import { ConnectionStateService } from '../services/connection-state.service';

/**
 * Network-resilience interceptor (зеркало сетевого error-UX мини-аппа).
 *
 * Что делает:
 *  - Детектит ОБРЫВ СВЯЗИ (не HTTP-ответ сервера): `HttpErrorResponse.status === 0`,
 *    `error.error instanceof ProgressEvent` (браузерный ERR_NETWORK/CORS/abort) и
 *    rxjs `TimeoutError` (из timeoutInterceptor).
 *  - Авто-ретраит ТОЛЬКО идемпотентные методы GET/HEAD с экспоненциальным бэкоффом.
 *    Мутации POST/PUT/PATCH/DELETE НЕ ретраятся И НЕ запоминаются для перевыпуска:
 *    status 0/timeout не доказывает, что мутация не применилась на сервере (ответ
 *    мог потеряться уже ПОСЛЕ), поэтому даже перевыпуск-по-клику дублировал бы
 *    необратимое действие. Повтор мутации — задача компонента (с идемпотентностью /
 *    реконсиляцией), а не глобального оверлея.
 *  - При исчерпании ретраев на сетевой ошибке выставляет ConnectionState → offline
 *    (показывается полноэкранный <app-connection-error>); на первом успешном
 *    ответе снимает offline.
 *
 * Порядок: этот интерсептор стоит ВНЕШНИМ (до timeoutInterceptor), чтобы каждая
 * ретрай-попытка GET получала СВЕЖИЙ таймаут (одиночный зависший запрос →
 * повтор с новым 30с-лимитом, а не один общий). `withoutRequestTimeout` для
 * выгрузок не затрагивается: они POST/multipart и под авто-ретрай не попадают.
 */

/**
 * BE-1303: запрос, которому нельзя поднимать полноэкранный «нет связи».
 *
 * Нужен для долгих необратимых мутаций (массовая рассылка уведомлений): она идёт на
 * сервере синхронно минутами, и обрыв на клиенте НЕ означает, что действие не
 * применилось. Оверлей в этом случае утверждает обратное и толкает оператора нажать
 * ещё раз — то есть разослать всё второй раз. Ошибка при этом никуда не девается:
 * её получает вызывающий код и объясняет по месту.
 *
 * ⚠️ По-хорошему это свойство ВСЕХ неидемпотентных запросов — ровно то же написано выше
 * про мутации, и сегодня оверлей одинаково неверно поднимают загрузки документов и
 * отчётов. Точечный отказ здесь — первый шаг, а не финальная форма: разворот дефолта
 * (оверлей только для GET/HEAD) меняет поведение всего приложения и делается отдельно.
 */
export const SUPPRESS_OFFLINE_OVERLAY = new HttpContextToken<boolean>(() => false);

/** HttpContext для такого запроса; свой контекст передайте, чтобы не потерять другие токены. */
export function withoutOfflineOverlay(context: HttpContext = new HttpContext()): HttpContext {
  return context.set(SUPPRESS_OFFLINE_OVERLAY, true);
}

/** Сколько раз повторить идемпотентный запрос при сетевой ошибке. */
const MAX_RETRIES = 2;
/** База экспоненциального бэкоффа, мс: 1-я пауза ~500, 2-я ~1000. */
const RETRY_BASE_DELAY_MS = 500;

export function isNetworkError(error: unknown): boolean {
  if (error instanceof TimeoutError) {
    return true;
  }
  if (error instanceof HttpErrorResponse) {
    // status 0 — ответ не получен (обрыв/CORS/abort); ProgressEvent — низкоуровневая
    // сетевая ошибка XHR. Любой реальный HTTP-статус (4xx/5xx) сюда НЕ попадает.
    return error.status === 0 || error.error instanceof ProgressEvent;
  }
  return false;
}

function isRetriableMethod(method: string): boolean {
  const m = method.toUpperCase();
  return m === 'GET' || m === 'HEAD';
}

export const networkResilienceInterceptor: HttpInterceptorFn = (req, next): Observable<HttpEvent<unknown>> => {
  const connectionState = inject(ConnectionStateService);
  const retriable = isRetriableMethod(req.method);

  return next(req).pipe(
    tap((event) => {
      // Любой успешный ответ от сервера => связь восстановлена, снять оверлей.
      if (event instanceof HttpResponse) {
        connectionState.setOnline();
      }
    }),
    retry({
      count: retriable ? MAX_RETRIES : 0,
      delay: (error, retryCount) => {
        // Ретраим ТОЛЬКО сетевые ошибки идемпотентных запросов; всё остальное
        // (4xx/5xx, ошибки рефреша и т.п.) пробрасываем немедленно.
        if (!retriable || !isNetworkError(error)) {
          return throwError(() => error);
        }
        const backoff = RETRY_BASE_DELAY_MS * Math.pow(2, retryCount - 1);
        return timer(backoff);
      },
    }),
    // Если после ретраев (или сразу, для мутаций) осталась СЕТЕВАЯ ошибка —
    // переходим в offline и показываем оверлей. Запрос для перевыпуска «Повторить»
    // отдаём ТОЛЬКО для идемпотентных GET/HEAD; для мутаций — без запроса (оверлей
    // покажется, но «Повторить» деградирует до dismiss, а повтор действия делает
    // компонент), иначе перевыпуск POST дублировал бы уже применённую мутацию.
    // Ошибку всё равно пробрасываем, чтобы вызывающий код отреагировал (register).
    tap({
      error: (error) => {
        if (isNetworkError(error) && !req.context.get(SUPPRESS_OFFLINE_OVERLAY)) {
          connectionState.setOffline(retriable ? req : undefined);
        }
      },
    })
  );
};
